import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTabNavigation } from '@contexts/TabNavigationContext'
import { parseTabLinks, getTabId } from '@utils/tabLinkParser'
import './MarkdownWithTabLinks.css'

interface MarkdownWithTabLinksProps {
  content: string
}

const MarkdownWithTabLinks: React.FC<MarkdownWithTabLinksProps> = ({ content }) => {
  const { navigateToTab } = useTabNavigation()

  // Custom components to parse and linkify tab names
  const components = useMemo(() => ({
    // Handle markdown links that contain tab names
    a: ({ children, href, ...props }: any) => {
      // Check if the link text is a tab name
      const linkText = typeof children === 'string' ? children :
                       (Array.isArray(children) && typeof children[0] === 'string') ? children[0] : null

      if (linkText) {
        const tabId = getTabId(linkText)
        if (tabId) {
          // This is a tab link - make it clickable to navigate to tab
          return (
            <span
              className="tab-link"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigateToTab(tabId)
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigateToTab(tabId)
                }
              }}
            >
              {children}
            </span>
          )
        }
      }

      // Regular link - render normally
      return <a href={href} {...props}>{children}</a>
    },

    p: ({ children, ...props }: any) => {
      // Process text nodes to add tab links
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child === 'string') {
          return parseTabLinks(child, (tabName) => {
            const tabId = getTabId(tabName)
            if (tabId) {
              navigateToTab(tabId)
            }
          })
        }
        return child
      })

      return <p {...props}>{processedChildren}</p>
    },

    strong: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child === 'string') {
          return parseTabLinks(child, (tabName) => {
            const tabId = getTabId(tabName)
            if (tabId) {
              navigateToTab(tabId)
            }
          })
        }
        return child
      })

      return <strong {...props}>{processedChildren}</strong>
    },

    li: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child === 'string') {
          return parseTabLinks(child, (tabName) => {
            const tabId = getTabId(tabName)
            if (tabId) {
              navigateToTab(tabId)
            }
          })
        }
        return child
      })

      return <li {...props}>{processedChildren}</li>
    },
  }), [navigateToTab])

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  )
}

export default MarkdownWithTabLinks
